import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  IconButton,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert
} from '@mui/material';
import { Upload, Trash2, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { apiService } from '../services/api';

const ReceiptUploader = ({ reportId, receipts, setReceipts, readOnly }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files.length) return;

    // Validate number of files
    if (files.length > 50) {
      setError('Maximum of 50 files allowed');
      return;
    }

    // Validate total number of receipts
    if (receipts.length + files.length > 50) {
      setError('Cannot exceed 50 total receipts');
      return;
    }

    // Validate file sizes
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    for (let i = 0; i < files.length; i++) {
      if (files[i].size > MAX_FILE_SIZE) {
        setError('File size cannot exceed 10MB');
        return;
      }
    }

    // Validate file types
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    for (let i = 0; i < files.length; i++) {
      if (!allowedTypes.includes(files[i].type)) {
        setError('Only JPG, PNG, and PDF files are allowed');
        return;
      }
    }

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('file', files[i]);
    }

    try {
      setUploading(true);
      setError(null);
      const uploadedReceipts = await apiService.uploadExpenseReceipts(reportId, formData);
      setReceipts([...receipts, ...uploadedReceipts]);
      setUploadDialogOpen(false);
    } catch (err) {
      setError(err.message || 'Failed to upload receipts');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteReceipt = async () => {
    if (!selectedReceipt) return;

    try {
      setError(null);
      await apiService.deleteExpenseReceipt(reportId, selectedReceipt.id);
      setReceipts(receipts.filter(r => r.id !== selectedReceipt.id));
      setConfirmDeleteOpen(false);
      setSelectedReceipt(null);
    } catch (err) {
      setError(err.message || 'Failed to delete receipt');
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Receipts</Typography>
        {!readOnly && (
          <Button
            startIcon={<Upload size={20} />}
            onClick={() => setUploadDialogOpen(true)}
            variant="contained"
          >
            Upload Receipts
          </Button>
        )}
      </Box>

      <Grid container spacing={2}>
        {receipts.map((receipt) => (
          <Grid item xs={12} sm={6} md={4} key={receipt.id}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography variant="subtitle1" noWrap sx={{ flex: 1 }}>
                    {receipt.original_filename}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => window.open(receipt.url, '_blank')}
                    >
                      <ExternalLink size={18} />
                    </IconButton>
                    {!readOnly && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setSelectedReceipt(receipt);
                          setConfirmDeleteOpen(true);
                        }}
                      >
                        <Trash2 size={18} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Uploaded: {format(new Date(receipt.created_at || receipt.upload_date), 'MM/dd/yyyy h:mm a')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
        {receipts.length === 0 && (
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" align="center">
              No receipts uploaded yet
            </Typography>
          </Grid>
        )}
      </Grid>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => !uploading && setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Receipts</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Select receipts to upload (JPG, PNG, or PDF format, max 10MB each)
          </DialogContentText>
          <input
            accept="image/jpeg,image/jpg,image/png,application/pdf"
            style={{ display: 'none' }}
            id="receipt-upload"
            type="file"
            multiple
            onChange={handleFileUpload}
            disabled={uploading}
          />
          <label htmlFor="receipt-upload">
            <Button
              variant="outlined"
              component="span"
              fullWidth
              disabled={uploading}
              startIcon={uploading ? <CircularProgress size={20} /> : <Upload size={20} />}
              sx={{ mt: 2 }}
            >
              {uploading ? 'Uploading...' : 'Choose Files'}
            </Button>
          </label>
          <Typography variant="caption" display="block" sx={{ mt: 1, textAlign: 'center' }}>
            Accepted formats: JPG, PNG, PDF (Maximum 50 files, 10MB each)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setUploadDialogOpen(false)} 
            disabled={uploading}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this receipt? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteReceipt}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReceiptUploader; 